import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { LOCALES, type Locale, type TKey } from './types';
import ko from './dict.ko';
import en from './dict.en';
import ja from './dict.ja';
import zh from './dict.zh';
import es from './dict.es';
import fr from './dict.fr';

const DICTS: Record<Locale, Record<TKey, string>> = { ko, en, ja, zh, es, fr };

function detectInitial(): Locale {
  if (typeof window === 'undefined') return 'ko';
  const stored = localStorage.getItem('bq.locale') as Locale | null;
  if (stored && LOCALES.includes(stored)) return stored;
  const raw = (navigator.language || 'ko').toLowerCase();
  const lang = raw.slice(0, 2);
  // zh-CN / zh-TW / zh-HK → 'zh'
  if (lang === 'zh') return 'zh';
  if ((LOCALES as readonly string[]).includes(lang)) return lang as Locale;
  return 'ko';
}

interface LocaleCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: TKey) => string;
}

const Ctx = createContext<LocaleCtx | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitial);

  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem('bq.locale', locale);
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
  }, []);

  const t = useCallback(
    (key: TKey): string => {
      const dict = DICTS[locale];
      const val = dict[key];
      if (val === undefined) {
        // Dev-friendly fallback: warn + return key
        if (import.meta.env.MODE !== 'production') {
          console.warn(`[i18n] missing key "${key}" for locale "${locale}"`);
        }
        return DICTS.en[key] ?? key;
      }
      return val;
    },
    [locale],
  );

  const value = useMemo<LocaleCtx>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTranslation(): LocaleCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTranslation must be used within <LocaleProvider>');
  return ctx;
}

export { LOCALES, LOCALE_LABEL, LOCALE_SHORT, type Locale, type TKey } from './types';
